import axios from '@/libs/axios'
import store from '@/store'
import { initialAbility } from '@/libs/acl/config'
import ability from '@/libs/acl/ability'
import { clearCompanyWindowGlobals } from '@/libs/companyGlobals'

const INTERVAL_MS = 10000

let timerId = null
let inFlight = false
let redirecting = false

export function isPublicAuthPath(path = '') {
  const normalized = String(path || '').split('?')[0]
  return normalized === '/'
    || normalized === ''
    || normalized.startsWith('/login')
    || normalized.startsWith('/register')
    || normalized.startsWith('/contact')
    || normalized.startsWith('/documentation')
    || normalized.startsWith('/faq')
    || normalized.startsWith('/forgot-password')
    || normalized.startsWith('/recovery')
    || normalized.startsWith('/reset-password')
    || normalized.startsWith('/system-status')
}

export function stopSessionWatch() {
  if (timerId != null) {
    clearInterval(timerId)
    timerId = null
  }
}

export function startSessionWatch() {
  stopSessionWatch()
  // Immediate check, then every 10s while the tab stays open.
  checkSessionAlive()
  timerId = setInterval(() => {
    checkSessionAlive()
  }, INTERVAL_MS)
}

export async function forceSessionExpiredRedirect({ blocked = false } = {}) {
  if (redirecting) {
    return
  }

  stopSessionWatch()

  try {
    store.commit('auth/SET_AUTHENTICATED', false)
    store.commit('auth/SET_USER', null)
    store.commit('modulesRegistry/CLEAR_MODULES', null, { root: true })
    ability.update(initialAbility)
    clearCompanyWindowGlobals()
  } catch (e) {
    // ignore
  }

  const path = typeof window !== 'undefined' ? (window.location?.pathname || '') : ''

  // Public pages stay open as guests; protected pages go to login.
  if (isPublicAuthPath(path)) {
    redirecting = false
    return
  }

  redirecting = true
  if (!path.includes('/login')) {
    window.location.assign(blocked ? '/login?blocked=1' : '/login')
  } else {
    redirecting = false
  }
}

export async function checkSessionAlive() {
  if (typeof window === 'undefined') {
    return true
  }

  if (!store.getters['auth/authenticated']) {
    stopSessionWatch()
    return false
  }

  if (inFlight || redirecting) {
    return true
  }

  inFlight = true
  try {
    // skipSessionRedirect: interceptor must not also handle this probe's 401
    await axios.get('api/user', { skipSessionRedirect: true })
    return true
  } catch (error) {
    const status = error?.response?.status
    if (status === 401 || status === 419) {
      const message = String(error?.response?.data?.message || '')
      const blocked = message.toLowerCase().includes('blocked')
      await forceSessionExpiredRedirect({ blocked })
      return false
    }
    // Network / 5xx: keep session locally; next tick will retry
    return true
  } finally {
    inFlight = false
  }
}
